import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { isNumber, isString } from 'lodash';
import { FC, useMemo } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import {
  locationsAccountAtom,
  locationsActivityAtom,
  locationsProductTypesAtom,
  locationsSelectedFeatureIdAtom,
} from 'src/atoms/filters/locations';
import { getProductLocationsOptions } from 'src/clients/locations/Device Locations API/@tanstack/react-query.gen';
import { PointClusterTileLayer } from 'src/locations/LocationsClusterLayer';
import { AppMap } from 'src/map/AppMap';

export const LocationsMap: FC = () => {
  const account = useAtomValue(locationsAccountAtom);
  const productTypes = useAtomValue(locationsProductTypesAtom);
  const activity = useAtomValue(locationsActivityAtom);
  const [selectedId, setSelectedId] = useAtom(locationsSelectedFeatureIdAtom);
  const queryClient = useQueryClient();
  const { 'device-locations': map } = useMap();

  const layers = useMemo(() => {
    if (account) {
      return [
        new PointClusterTileLayer({
          id: 'locations-tiles',
          updateTriggers: {
            getTileData: [productTypes, account, activity],
            renderSubLayers: [selectedId],
          },
          debounceTime: 500,
          queryClient,
          account,
          productTypes,
          maxZoom: 22,
          minZoom: 0,
          pickable: true,
          onClick(pickingInfo) {
            const { layer, object } = pickingInfo;

            if (layer instanceof PointClusterTileLayer) {
              if (object?.properties?.cluster) {
                map?.flyTo({
                  zoom: map?.getZoom() + 1,
                  center: object.geometry.coordinates,
                });

                return true;
              }

              if (map && (isString(object.properties?.id) || isNumber(object.properties?.id))) {
                const canvasHeight = map.getCanvas().offsetHeight;
                map.flyTo({
                  center: object.geometry.coordinates,
                  padding: { bottom: canvasHeight * 0.8 },
                });
                setSelectedId(object.properties.imsi.toString());

                return true;
              }

              return true;
            }
          },
          getTileData: async (tile) => {
            const { index, signal } = tile;
            const { x, y, z } = index;

            const options = getProductLocationsOptions({
              query: {
                TileLocationX: x,
                TileLocationY: y,
                ZoomLevel: z,
                AccountReferenceId: account,
                ProductTypes: productTypes,
                ...(activity && { ActivityDays: activity }),
              },
              signal,
            });

            const data = await queryClient.fetchQuery({
              ...options,
              staleTime: 360 * 1000,
              meta: { isLocations: true, isLocationsMap: true },
            });

            return data;
          },
        }),
      ];
    }

    return [];
  }, [productTypes, account, activity, selectedId, map]);

  const initialViewState = { zoom: 7, longitude: -0.7267502, latitude: 51.8316436 };

  return (
    <AppMap
      testId="locations.mapSection.map"
      id="device-locations"
      initalViewState={initialViewState}
      layers={layers}
      disabled={account === null}
    />
  );
};
